import { useEffect, useState } from 'react';
import { Button } from 'subframe/components/Button';
import * as SubframeCore from '@subframe/core';
import { Switch } from 'subframe/components/Switch';
import { Badge } from 'subframe/components/Badge';
import { InformationalHeader } from 'subframe/components/InformationalHeader';
import { InfoTooltip } from 'subframe/components/InfoTooltip';
import { Avatar } from 'subframe/components/Avatar';
import { Alert, Breadcrumbs } from 'subframe/index';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import useUserAccountState from 'hooks/useUserAccountState';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import Page from 'components/Page';
import styles from '../AcmeUpgradesClusters.module.scss';
import { Actions, AtomicTooltip } from 'components/design-system';
import { RouterLink } from 'components/RouterLink';
import { updateUpgradeAssessment, useListSubscriptions } from 'api/frontend';
import {
  useListClustersInfinite,
  useListUpgradeAssessmentsInfinite,
} from 'api/frontend-infinite';
import { clusterFlattenPages, flattenPages } from 'utils/arrays';
import { UPGRADE_ASSESSMENTS } from 'src/data/upgrades';
import { ConsoleLoader } from 'components/Loader';
import BaseTable from 'components/design-system/Table/BaseTable';
import { getHumanReadableDateAndTime } from 'utils/formatTime';
import {
  getTooltipForUpgradeRequestedBy,
  getUpgradeAssessmentStatusBadge,
  UpgradeViewMode,
} from '../helpers/UpgradesHelpers';
import {
  Cluster,
  ResolvedEntitlementFeaturesItem,
  UpgradeAssessment,
} from 'api/models';
import { getClusterProvider, getClusterRegion } from 'utils/clusterStates';
import { ChangeNameDialog } from '../helpers/ChangeNameDialog';
import { somethingWentWrong, toastAutoHideDuration } from 'constants/toasts';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { CLUSTERS } from 'src/data/version_register_cp';
import { getMostRelevanSubscription } from 'utils/subscription_plan';
import FeatureAccessDialog from 'components/dialogs/FeatureAccessDialog';
import MultiSelectDropdown from 'components/MultiSelectDropdown';
import { openUpgradeExportInNewWindow } from '../helpers/export_full';
import MarkUpgradeAssessmentAsArchived from '../components/MarkUpgradeAssessmentAsArchived';
import { useExampleData } from 'hooks/useExampleData';
import { ReloadButton } from 'components/ReloadButton';

export default function UpgradesAssessmentsIndex() {
  const { logEvent } = AnalyticsEventLogger();
  const { enqueueSnackbar } = useSnackbar();
  const { account, currentOrganization } = useUserAccountState();
  const navigate = useNavigate();
  const [showExampleData, setShowExampleData] = useExampleData();
  const basePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/assessments',
  );
  const templatesBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/upgrades/templates/clusters',
  );
  const clustersBasePath = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/clusters',
  );

  const [selected, setSelected] = useState<string[]>(['pending', 'ready']);

  // Clusters
  const { data: clustersList } = useListClustersInfinite(
    '',
    {},
    {
      request: { headers: { Authorization: `Bearer ${account?.token}` } },
    },
    {
      initialSize: 100,
      revalidateFirstPage: false,
    },
  );

  const clusters = showExampleData
    ? CLUSTERS
    : clusterFlattenPages(clustersList || []) || [];

  const realClusters = clusters
    ? clusters.filter((c) => c.internal_k8s_ref !== 'example-cluster-ref')
    : [];

  useEffect(() => {
    if (clusters && sessionStorage.getItem('example-data') === null) {
      setShowExampleData(!realClusters.length);
    }
  }, [clusters]);

  //Subscriptions
  const { data: Subscriptions } = useListSubscriptions(
    currentOrganization?.slug,
    {},
    {
      request: { headers: { Authorization: `Bearer ${account?.token}` } },
    },
  );
  const currentSubscription = getMostRelevanSubscription(
    Subscriptions?.subscriptions,
  );
  const showTemplateLock = Subscriptions
    ? !Subscriptions?.total_entitlement?.features?.includes(
        'request_upgrade_template',
      )
    : true;

  // Upgrade Assessments
  const { data: upgradeAssessmentsData, mutate } =
    useListUpgradeAssessmentsInfinite(
      {},
      {
        request: {
          headers: { Authorization: `Bearer ${account.token}` },
        },
      },
    );
  const upgradeAssessments = showExampleData
    ? UPGRADE_ASSESSMENTS
    : (flattenPages(upgradeAssessmentsData) || []).sort((a, b) => {
        return a.created > b.created ? -1 : 1; // Latest requested Upgrade Assessment should appear on top
      });

  const upgradeAssessmentsToShow = upgradeAssessments.filter((ua) => {
    if (selected.length < 1) {
      return true;
    }

    if (ua.status === 'ready' || ua.status === 'outdated') {
      return selected.includes('ready');
    } else if (ua.status === 'in-progress' || ua.status === 'pending') {
      return selected.includes('pending');
    } else if (ua.status === 'archived') {
      return selected.includes('archived');
    } else if (ua.status === 'failed') {
      return selected.includes('failed');
    } else {
      return false;
    }
  });

  const [openUpgradeSubscriptionDialog, setOpenUpgradeSubscriptionDialog] =
    useState(false);
  const [openEditNameDialog, setOpenEditNameDialog] = useState(false);
  const [
    openMarkUpgradeAssessmentAsArchivedDialog,
    setOpenMarkUpgradeAssessmentAsArchivedDialog,
  ] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<
    UpgradeAssessment | undefined
  >(undefined);

  return (
    <Page title="Upgrade Assessments">
      <div
        className={styles['pageContents']}
        style={{ marginLeft: '32px', width: 'calc(100% - 32px' }}
      >
        <Breadcrumbs>
          <Breadcrumbs.Item>Upgrade Copilot</Breadcrumbs.Item>
          <Breadcrumbs.Divider name="FeatherChevronRight" />
          <Breadcrumbs.Item>Upgrade Assessments</Breadcrumbs.Item>
        </Breadcrumbs>
        <div className="flex w-full flex-col items-start gap-2">
          <div className="flex w-full items-center justify-between">
            <span className="text-section-header font-section-header text-default-font">
              Upgrade Assessments
            </span>
            <div className="flex items-center gap-2">
              <ReloadButton reload={mutate} />
              <RouterLink to={`${basePath}/new`}>
                <Button disabled={false} size="large" icon="FeatherPlus">
                  Request Upgrade Assessment
                </Button>
              </RouterLink>
            </div>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Overview of requested and available Upgrade Assessments
            </span>
            <div className="flex grow shrink-0 basis-0 items-center justify-end gap-2">
              <span className="text-body font-body text-subtext-color">
                Show example data
              </span>
              <AtomicTooltip
                data-cy="show-example-data-tooltip"
                tooltipContent={
                  showExampleData
                    ? 'Show results from your onboarded clusters'
                    : 'Show example data'
                }
              >
                <Switch
                  data-cy="switch"
                  checked={showExampleData}
                  onCheckedChange={() => {
                    setShowExampleData(!showExampleData);
                  }}
                />
              </AtomicTooltip>
            </div>
          </div>
        </div>
        {showExampleData && (
          <Alert
            variant="warning"
            title="You are currently viewing example data"
            description={`To view results from your onboarded clusters, toggle the "Show example data" button`}
          />
        )}
        {!showExampleData &&
          (clusters === undefined || upgradeAssessmentsData === undefined) && (
            <ConsoleLoader />
          )}
        {!showExampleData && !!clustersList && realClusters.length === 0 && (
          <div className="flex items-center gap-2">
            <span className="text-body font-body text-default-font">
              No clusters onboarded so far. Click here to onboard your first
              cluster
            </span>
            <RouterLink to={`${clustersBasePath}/new`}>
              <Button
                size="small"
                icon="FeatherPlus"
                onClick={() =>
                  logEvent('upgrades-templates-onboard-cluster-btn-click')
                }
              >
                Add Cluster
              </Button>
            </RouterLink>
          </div>
        )}
        {(showExampleData ||
          (upgradeAssessmentsData !== undefined &&
            realClusters.length > 0)) && (
          <>
            {!!upgradeAssessments.length && (
              <div className="flex w-full flex-col items-start gap-2">
                <div className="flex w-full grow shrink-0 basis-0 items-center gap-2">
                  <InformationalHeader className="h-auto grow shrink-0 basis-0 self-stretch">
                    <InformationalHeader.Item
                      title="Total"
                      iconName={null}
                      headingSlot={
                        <InfoTooltip tooltipText="Total Upgrade Assessments requested" />
                      }
                      bodySlot={
                        <span className="text-body-bold font-body-bold text-default-font">
                          {upgradeAssessments.length}
                        </span>
                      }
                    />
                    <InformationalHeader.Item
                      title="Waiting for Assessment"
                      iconName={null}
                      headingSlot={
                        <InfoTooltip tooltipText="Count of Upgrade Assessments currently generating" />
                      }
                      bodySlot={
                        <span className="text-body-bold font-body-bold text-default-font">
                          {
                            upgradeAssessments.filter(
                              (a) =>
                                a.status === 'pending' ||
                                a.status === 'in-progress',
                            ).length
                          }
                        </span>
                      }
                    />
                    <InformationalHeader.Item
                      title="Available"
                      iconName={null}
                      headingSlot={
                        <InfoTooltip tooltipText="Count of Upgrade Assessments ready for review" />
                      }
                      bodySlot={
                        <span className="text-body-bold font-body-bold text-default-font">
                          {
                            upgradeAssessments.filter(
                              (a) =>
                                a.status === 'ready' || a.status === 'outdated',
                            ).length
                          }
                        </span>
                      }
                    />
                  </InformationalHeader>
                  <MultiSelectDropdown
                    selectAdditionalClassNames="w-56"
                    selectTruncationLength={24}
                    emptyLabel="Status"
                    listHeader="Filter by Status"
                    options={[
                      { value: 'pending', label: 'Waiting for Assessment' },
                      { value: 'ready', label: 'Available' },
                      { value: 'archived', label: 'Archived' },
                    ]}
                    selected={selected}
                    setSelected={setSelected}
                    iconName={selected.length > 0 ? 'FeatherFilter' : null}
                    variant={selected.length > 0 ? 'filled' : 'outline'}
                  />
                </div>
                <div className="flex w-full flex-col items-start gap-12">
                  <div className="flex w-full flex-col items-start gap-2">
                    <BaseTable
                      settings={{
                        localStorageKey: 'upgrade_assessments-index-view',
                        dataCyPrefix: 'upgrade-assessments-index-view',
                      }}
                      data={upgradeAssessmentsToShow}
                      rowLink={(row) => `${basePath}/${row.id}`}
                      rowSettings={{
                        preventClick: (row) =>
                          (row.id !==
                            'upgrasses_2e6323a4-576b-4d32-99da-70204ee00981' &&
                            row.id !==
                              'upgrasses_c4aa47e1-f865-46d4-8b2d-3a15165e6392' &&
                            showExampleData) ||
                          (row.status !== 'ready' && row.status !== 'archived'),
                        preventClickToastMessage:
                          'This Upgrade Assessment is not ready yet',
                      }}
                      columns={[
                        {
                          id: '1',
                          title: 'Upgrade Assessment',
                          cellType: 'cell',
                          cellStyle: { height: '96px' },
                          render: (item) => {
                            let cluster: Cluster | undefined = undefined;
                            if (
                              item.resources.clusters !== undefined &&
                              item.resources.clusters.length > 0
                            ) {
                              cluster = clusters.find(
                                (c) =>
                                  c.id ===
                                  (item.resources.clusters || [])[0].id,
                              );
                            }

                            return (
                              <div className="flex flex-col items-start gap-2">
                                <div className="flex items-center justify-center gap-2">
                                  <span className="text-body-bold font-body-bold text-neutral-700">
                                    {item.name}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-2">
                                    {item.environment ? (
                                      <Badge variant="neutral">
                                        {item.environment}
                                      </Badge>
                                    ) : (
                                      <span className="text-label font-label text-subtext-color">
                                        {'Environment: n/a'}
                                      </span>
                                    )}
                                    <span className="text-label font-label text-subtext-color">
                                      |
                                    </span>
                                    <span className="text-label font-label text-subtext-color">
                                      {cluster === undefined
                                        ? 'Provider: n/a'
                                        : getClusterProvider(cluster)}
                                    </span>
                                    <span className="text-label font-label text-subtext-color">
                                      |
                                    </span>
                                    <span className="text-label font-label text-subtext-color">
                                      {cluster === undefined
                                        ? 'Region: n/a'
                                        : getClusterRegion(cluster)}
                                    </span>
                                    <span className="text-label font-label text-subtext-color">
                                      |
                                    </span>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <div className="flex items-start gap-2">
                                      <span className="text-label font-label text-subtext-color">
                                        From:{' '}
                                        {item.current_version ||
                                          cluster?.version ||
                                          'n/a'}
                                      </span>
                                      {item.desired_version && (
                                        <>
                                          <span className="text-label font-label text-subtext-color">
                                            |
                                          </span>
                                          <span className="text-label font-label text-subtext-color">
                                            To: {item.desired_version}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          },
                        },
                        {
                          id: '2',
                          title: 'Status',
                          cellType: 'cell',
                          cellStyle: { height: '96px' },
                          render: (item) => {
                            return (
                              <AtomicTooltip
                                tooltipContent={
                                  getUpgradeAssessmentStatusBadge(item.status)
                                    .tooltip ||
                                  getUpgradeAssessmentStatusBadge(item.status)
                                    .title
                                }
                              >
                                <Badge
                                  variant={
                                    getUpgradeAssessmentStatusBadge(item.status)
                                      .variant
                                  }
                                  style={{
                                    maxWidth: '150px',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {
                                    getUpgradeAssessmentStatusBadge(item.status)
                                      .title
                                  }
                                </Badge>
                              </AtomicTooltip>
                            );
                          },
                        },
                        {
                          id: '3',
                          title: 'Requested',
                          cellType: 'text-cell',
                          cellStyle: {
                            width: '20%',
                            height: '96px',
                          },
                          render: (item) => {
                            return item.created
                              ? getHumanReadableDateAndTime(item.created)
                              : '';
                          },
                        },
                        {
                          id: '4',
                          title: 'Requested By',
                          cellType: 'cell',
                          cellStyle: {
                            width: '5%',
                            height: '96px',
                          },
                          render: (item) => {
                            return (
                              <AtomicTooltip
                                className="ph-no-capture"
                                tooltipContent={getTooltipForUpgradeRequestedBy(
                                  item.requested_by?.name,
                                  item.created,
                                  UpgradeViewMode.Assessment,
                                )}
                              >
                                {item.requested_by?.picture ? (
                                  <Avatar
                                    size="x-small"
                                    image={item.requested_by?.picture}
                                    className="ph-no-capture"
                                  />
                                ) : (
                                  <Avatar variant="neutral" size="x-small">
                                    {item.requested_by?.name
                                      ?.charAt(0)
                                      ?.toUpperCase()}
                                  </Avatar>
                                )}
                              </AtomicTooltip>
                            );
                          },
                        },
                      ]}
                      actions={[
                        {
                          id: 'actions',
                          render: (item) => {
                            const markdownExports = (item.exports || []).filter(
                              (e) => e.format === 'markdown',
                            );
                            // TODO(stefan: re-enable once PDF export works
                            // const pdfExports = (item.exports || []).filter(
                            //   (e) => e.format === 'pdf',
                            // );

                            return (
                              <div className={styles['stack-1b79c295']}>
                                <div
                                  className={styles['stack-1c3bdfbc']}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                  }}
                                >
                                  <Actions
                                    sections={[
                                      {
                                        title: 'Update Properties',
                                        items: [
                                          <Actions.Item
                                            key="edit-name"
                                            title="Edit Name"
                                            icon="FeatherEdit2"
                                            onClick={() => {
                                              setSelectedAssessment(item);
                                              setOpenEditNameDialog(true);
                                            }}
                                            disabled={showExampleData}
                                            tooltip={
                                              showExampleData
                                                ? 'Example Upgrade Assessments cannot be updated'
                                                : ''
                                            }
                                          />,
                                        ],
                                      },
                                      {
                                        title: 'Export',
                                        items: [
                                          <Actions.Item
                                            key="export-as-markdown"
                                            title="Export as Markdown"
                                            icon="FeatherCode2"
                                            onClick={() => {
                                              openUpgradeExportInNewWindow(
                                                UpgradeViewMode.Assessment,
                                                item.id,
                                                account.token,
                                                enqueueSnackbar,
                                              );
                                            }}
                                            disabled={
                                              (showExampleData &&
                                                item.id !==
                                                  'upgrasses_2e6323a4-576b-4d32-99da-70204ee00981' &&
                                                item.id !==
                                                  'upgrasses_c4aa47e1-f865-46d4-8b2d-3a15165e6392') ||
                                              item.status === 'pending' ||
                                              item.status === 'in-progress' ||
                                              markdownExports.length < 1
                                            }
                                            tooltip={
                                              showExampleData &&
                                              item.id !==
                                                'upgrasses_2e6323a4-576b-4d32-99da-70204ee00981' &&
                                              item.id !==
                                                'upgrasses_c4aa47e1-f865-46d4-8b2d-3a15165e6392'
                                                ? 'Example Upgrade Assessments cannot be exported'
                                                : item.status === 'pending' ||
                                                  item.status ===
                                                    'in-progress' ||
                                                  markdownExports.length < 1
                                                ? 'Action is not available for this Upgrade Assessment'
                                                : ''
                                            }
                                          />,

                                          // <Actions.Item
                                          //   key="export-as-pdf"
                                          //   title="Export as PDF"
                                          //   icon="FeatherFileText"
                                          //   onClick={() => {
                                          //     window.open(
                                          //       pdfExports[0].url,
                                          //       '_blank',
                                          //       'noopener,noreferrer',
                                          //     );
                                          //   }}
                                          //   disabled={
                                          //     (showExampleData &&
                                          //       item.id !== 'upgrasses_2e6323a4-576b-4d32-99da-70204ee00981' &&
                                          //        item.id !== 'upgrasses_c4aa47e1-f865-46d4-8b2d-3a15165e6392') ||
                                          //     item.status === 'pending' ||
                                          //     item.status === 'in-progress' ||
                                          //     pdfExports.length < 1
                                          //   }
                                          //   tooltip={
                                          //     showExampleData &&
                                          //       item.id !== 'upgrasses_2e6323a4-576b-4d32-99da-70204ee00981' &&
                                          //        item.id !== 'upgrasses_c4aa47e1-f865-46d4-8b2d-3a15165e6392'
                                          //       ? 'Example Upgrade Assessments cannot be exported'
                                          //       : item.status === 'pending' ||
                                          //         item.status === 'in-progress' ||
                                          //         pdfExports.length < 1
                                          //         ? 'Action is not available for this Upgrade Assessment'
                                          //         : ''
                                          //   }
                                          // />,
                                        ],
                                      },
                                      {
                                        title: 'Lifecycle Actions',
                                        items: [
                                          <Actions.Item
                                            key="request-template"
                                            title="Request Upgrade Template"
                                            icon="FeatherPlus"
                                            disabled={showExampleData}
                                            tooltip={
                                              showExampleData
                                                ? 'Example Upgrade Assessments cannot be used to create Upgrade Templates'
                                                : ''
                                            }
                                            onClick={(event) => {
                                              if (
                                                showTemplateLock &&
                                                !showExampleData
                                              ) {
                                                event.stopPropagation();
                                                event.preventDefault();
                                                setSelectedAssessment(item);
                                                setOpenUpgradeSubscriptionDialog(
                                                  true,
                                                );
                                              } else {
                                                let filter = '';
                                                if (
                                                  item.resources.clusters?.at(0)
                                                ) {
                                                  filter = `?cluster_id=${
                                                    (
                                                      item.resources.clusters?.at(
                                                        0,
                                                      ) || { id: '' }
                                                    ).id
                                                  }`;
                                                }
                                                navigate(
                                                  `${templatesBasePath}/new${filter}`,
                                                );
                                              }
                                            }}
                                            subscriptionLockIcon={
                                              showTemplateLock
                                            }
                                          />,
                                          <Actions.Item
                                            key="mark-archived"
                                            title="Archive"
                                            icon="FeatherArchive"
                                            onClick={() => {
                                              setSelectedAssessment(item);
                                              setOpenMarkUpgradeAssessmentAsArchivedDialog(
                                                true,
                                              );
                                            }}
                                            disabled={
                                              showExampleData ||
                                              item.status !== 'ready'
                                            }
                                            tooltip={
                                              showExampleData
                                                ? 'Action is not available on example data'
                                                : item.status !== 'ready'
                                                ? 'Action is only available for Available Upgrade Assessments'
                                                : ''
                                            }
                                          />,
                                        ],
                                      },
                                    ]}
                                  />
                                </div>

                                {item.status !== 'pending' &&
                                  item.status !== 'in-progress' && (
                                    <SubframeCore.Icon
                                      className={styles['bodyText']}
                                      name="FeatherChevronRight"
                                    />
                                  )}
                                {(item.status === 'pending' ||
                                  item.status === 'in-progress') && (
                                  <SubframeCore.Icon
                                    className={styles['icon-ef8c001b']}
                                    name="FeatherChevronRight"
                                  />
                                )}
                              </div>
                            );
                          },
                        },
                      ]}
                    />
                    {selectedAssessment && (
                      <>
                        <ChangeNameDialog
                          key="edit-name"
                          open={openEditNameDialog}
                          setOpen={setOpenEditNameDialog}
                          mode={UpgradeViewMode.Assessment}
                          name={selectedAssessment?.name || ''}
                          onConfirm={async (newName) => {
                            if (showExampleData) {
                              enqueueSnackbar(
                                'Action is not available on example data',
                                {
                                  variant: 'warning',
                                  autoHideDuration: toastAutoHideDuration,
                                },
                              );
                            } else {
                              try {
                                await updateUpgradeAssessment(
                                  selectedAssessment?.id || '',
                                  { name: newName },
                                  {
                                    headers: {
                                      Authorization: `Bearer ${account.token}`,
                                    },
                                  },
                                );
                                logEvent('upgrades-assessments-name-change', {
                                  template: selectedAssessment?.id || '',
                                  name: newName,
                                });
                                mutate();
                                enqueueSnackbar(
                                  'Successfully edited the requested properties of the Upgrade Assessment',
                                  {
                                    variant: 'success',
                                    autoHideDuration: toastAutoHideDuration,
                                  },
                                );
                              } catch (err) {
                                enqueueSnackbar(
                                  somethingWentWrong.replace(
                                    '<action>',
                                    'updating requested properties of the Upgrade Assessment',
                                  ),
                                  {
                                    variant: 'error',
                                    autoHideDuration: toastAutoHideDuration,
                                  },
                                );
                              }
                            }
                          }}
                        />
                        <FeatureAccessDialog
                          upgradePlanType={'cluster'}
                          open={openUpgradeSubscriptionDialog}
                          setOpen={setOpenUpgradeSubscriptionDialog}
                          currentPlan={currentSubscription?.plan || 'FreeTrial'}
                          feature={
                            ResolvedEntitlementFeaturesItem.request_upgrade_template
                          }
                        />
                        <MarkUpgradeAssessmentAsArchived
                          assessmentId={selectedAssessment.id}
                          mutateAssessment={mutate}
                          open={openMarkUpgradeAssessmentAsArchivedDialog}
                          onClose={() =>
                            setOpenMarkUpgradeAssessmentAsArchivedDialog(false)
                          }
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            {!upgradeAssessments.length && (
              <div>
                <span className="text-body font-body text-subtext-color">
                  No Upgrade Assessments requested so far.
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </Page>
  );
}
